import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { formatDate, getStatusColor, getStatusLabel, cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  StarIcon,
} from '@heroicons/react/24/outline'

interface User {
  _id: string
  email: string
  name?: string
  status: string
  isPremium: boolean
  progress: {
    currentLevel: number
    totalPoints: number
  }
  createdAt: string
  lastActiveAt?: string
}

interface UsersResponse {
  users: User[]
}

interface ApiResponse {
  data: UsersResponse
  meta: {
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }
}

export default function UsersPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [isPremium, setIsPremium] = useState('')
  
  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search, status, isPremium],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      })
      if (search) params.append('search', search)
      if (status) params.append('status', status)
      if (isPremium) params.append('isPremium', isPremium)
      
      const res = await api.get<ApiResponse>(`/admin/users?${params}`)
      return res.data
    },
  })
  
  const togglePremiumMutation = useMutation({
    mutationFn: async ({ userId, isPremium }: { userId: string; isPremium: boolean }) => {
      await api.post(`/admin/users/${userId}/premium`, { isPremium })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Статус Premium обновлён')
    },
    onError: () => {
      toast.error('Ошибка обновления статуса')
    },
  })
  
  const handleExport = async () => {
    try {
      const response = await api.get('/admin/users/export', {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'users.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Экспорт завершён')
    } catch {
      toast.error('Ошибка экспорта')
    }
  }
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Пользователи</h1>
        <button onClick={handleExport} className="btn-secondary">
          <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
          Экспорт CSV
        </button>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Поиск по email или имени..."
                className="w-full pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
              />
            </div>
          </div>
          
          <select
            className="w-40"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Все статусы</option>
            <option value="active">Активные</option>
            <option value="blocked">Заблокированные</option>
          </select>
          
          <select
            className="w-40"
            value={isPremium}
            onChange={(e) => {
              setIsPremium(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Все</option>
            <option value="true">Premium</option>
            <option value="false">Обычные</option>
          </select>
        </div>
      </div>
      
      {/* Table */}
      <div className="card overflow-hidden p-0">
        <table className="table">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Статус</th>
              <th>Premium</th>
              <th>Уровень</th>
              <th>Баллы</th>
              <th>Дата регистрации</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">
                  Загрузка...
                </td>
              </tr>
            ) : data?.data.users.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">
                  Пользователи не найдены
                </td>
              </tr>
            ) : (
              data?.data.users.map((user) => (
                <tr
                  key={user._id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/users/${user._id}`)}
                >
                  <td>
                    <div>
                      <p className="font-medium text-white">{user.email}</p>
                      {user.name && (
                        <p className="text-sm text-gray-400">{user.name}</p>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusColor(user.status)}>
                      {getStatusLabel(user.status)}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        togglePremiumMutation.mutate({
                          userId: user._id,
                          isPremium: !user.isPremium,
                        })
                      }}
                      className={cn(
                        'p-1.5 rounded-lg transition-colors',
                        user.isPremium
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-700 text-gray-400 hover:text-yellow-400'
                      )}
                    >
                      <StarIcon className="w-5 h-5" />
                    </button>
                  </td>
                  <td className="text-white">{user.progress.currentLevel}</td>
                  <td className="text-white">{user.progress.totalPoints}</td>
                  <td className="text-gray-400">{formatDate(user.createdAt)}</td>
                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/users/${user._id}`)
                      }}
                      className="btn-secondary btn-sm"
                    >
                      Открыть
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {/* Pagination */}
        {data && data.meta.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Показано {((page - 1) * 20) + 1}-{Math.min(page * 20, data.meta.pagination.total)} из {data.meta.pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                className="btn-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Назад
              </button>
              <button
                className="btn-secondary btn-sm"
                disabled={page >= data.meta.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Вперёд
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
